import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState, StatusBadge } from "../components/shared";
import { formatMoney, CURRENCIES, can } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, X, PackageCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2);

export default function PurchaseOrders() {
  const { user } = useAuth();
  const editable = can(user.role, "po");
  const base = user.organization?.base_currency || "NGN";
  const [items, setItems] = useState([]);
  const [raws, setRaws] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { supplier_id: "", warehouse_id: "", currency: base, items: [], status: "ordered", notes: "" };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    const [po, r, s, w] = await Promise.all([
      api.get("/purchase-orders"), api.get("/raw-materials"), api.get("/contacts?type=supplier"), api.get("/warehouses"),
    ]);
    setItems(po.data); setRaws(r.data); setSuppliers(s.data); setWarehouses(w.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...empty, supplier_id: suppliers[0]?.id || "", warehouse_id: warehouses[0]?.id || "" }); setOpen(true); };
  const rawsInWh = raws.filter((r) => r.warehouse_id === form.warehouse_id);
  const addItem = () => setForm((f) => { const r = rawsInWh[0]; return { ...f, items: [...f.items, { raw_material_id: r?.id || "", quantity: 1, cost: r?.unit_cost || 0, _key: uid() }] }; });
  const setItem = (i, k, v) => setForm((f) => { const it = [...f.items]; it[i] = { ...it[i], [k]: k === "raw_material_id" ? v : parseFloat(v || 0) }; if (k === "raw_material_id") { const r = raws.find((x) => x.id === v); it[i].cost = r?.unit_cost || 0; } return { ...f, items: it }; });
  const rmItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const total = form.items.reduce((s, it) => s + it.quantity * it.cost, 0);

  const save = async (e) => {
    e.preventDefault();
    if (form.items.length === 0) return toast.error("Add at least one item");
    const payload = { ...form, items: form.items.map(({ raw_material_id, quantity, cost }) => ({ raw_material_id, quantity, cost })) };
    try { await api.post("/purchase-orders", payload); toast.success(form.status === "received" ? "PO received — raw stock updated" : "PO created"); setOpen(false); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const receive = async (id) => { try { await api.put(`/purchase-orders/${id}/receive`); toast.success("Received — raw stock increased"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const del = async (id) => { if (!window.confirm("Delete PO?")) return; await api.delete(`/purchase-orders/${id}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="purchase-orders-page">
      <PageHeader title="Purchase Orders" subtitle="Buy raw materials — receiving increases raw stock"
        action={editable && <Button className="rounded-none gap-2" data-testid="create-po-btn" onClick={openNew} disabled={raws.length === 0 || suppliers.length === 0}><Plus className="w-4 h-4" /> New PO</Button>} />

      {(raws.length === 0 || suppliers.length === 0) && editable && (
        <div className="border border-border bg-secondary/40 p-3 text-sm text-muted-foreground mb-4">Add at least one raw material and one supplier to create purchase orders.</div>
      )}

      {items.length === 0 ? <EmptyState title="No purchase orders yet" /> : (
        <div className="border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-secondary">
              <TableHead>Number</TableHead><TableHead>Supplier</TableHead><TableHead>Date</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((po) => (
                <TableRow key={po.id} className="hover:bg-secondary/60" data-testid="po-row">
                  <TableCell className="font-mono font-semibold">{po.number}</TableCell>
                  <TableCell>{po.supplier_name}</TableCell>
                  <TableCell className="font-mono text-sm">{(po.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell><StatusBadge status={po.received ? "received" : po.status} /></TableCell>
                  <TableCell className="text-right font-mono font-bold">{formatMoney(po.total, po.currency)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {editable && !po.received && <Button size="sm" variant="outline" className="rounded-none gap-1" onClick={() => receive(po.id)} data-testid="receive-po-btn"><PackageCheck className="w-3.5 h-3.5" /> Receive</Button>}
                      {editable && <Button size="icon" variant="ghost" className="h-8 w-8 rounded-none text-destructive" onClick={() => del(po.id)}><Trash2 className="w-4 h-4" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">New Purchase Order</DialogTitle><DialogDescription>Order raw materials from a supplier; receiving increases raw stock.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Supplier</Label>
                <Select value={form.supplier_id} onValueChange={(v) => setForm((f) => ({ ...f, supplier_id: v }))}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="po-supplier-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Warehouse (destination)</Label>
                <Select value={form.warehouse_id} onValueChange={(v) => setForm((f) => ({ ...f, warehouse_id: v, items: [] }))}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="po-warehouse-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="ordered">Ordered</SelectItem><SelectItem value="received">Received (add stock now)</SelectItem></SelectContent>
                </Select>
              </div>
            </div>

            <div className="border border-border p-3 bg-secondary/40">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-xs uppercase tracking-wider">Line items (raw materials in {warehouses.find((w) => w.id === form.warehouse_id)?.name || "warehouse"})</Label>
                <Button type="button" size="sm" variant="outline" className="rounded-none gap-1" onClick={addItem} disabled={rawsInWh.length === 0} data-testid="add-po-item-btn"><Plus className="w-3.5 h-3.5" /> Add item</Button>
              </div>
              {rawsInWh.length === 0 && <p className="text-xs text-destructive">No raw materials in this warehouse.</p>}
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={it._key || i} className="flex flex-wrap gap-2 items-center" data-testid="po-item-row">
                    <Select value={it.raw_material_id} onValueChange={(v) => setItem(i, "raw_material_id", v)}>
                      <SelectTrigger className="rounded-none flex-1 min-w-[150px]"><SelectValue /></SelectTrigger>
                      <SelectContent>{rawsInWh.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" step="any" className="rounded-none w-20" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} placeholder="Qty" />
                    <Input type="number" step="any" className="rounded-none w-28" value={it.cost} onChange={(e) => setItem(i, "cost", e.target.value)} placeholder="Cost" />
                    <Button type="button" size="icon" variant="ghost" className="h-9 w-9 rounded-none text-destructive" onClick={() => rmItem(i)}><X className="w-4 h-4" /></Button>
                  </div>
                ))}
                {form.items.length === 0 && <p className="text-xs text-muted-foreground">No items added.</p>}
              </div>
            </div>

            <div className="border border-border p-3 flex justify-between font-mono font-bold">
              <span>Total</span><span data-testid="po-total">{formatMoney(total, form.currency)}</span>
            </div>

            <DialogFooter><Button type="submit" className="rounded-none" data-testid="save-po-btn">Create PO</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
